import React, { FC, useCallback, useEffect, useState, FormEvent } from 'react'
import { get } from 'lodash'
import cx from 'classnames'
import {
  Classes,
  Collapse,
  Dialog,
  ControlGroup,
  Intent,
  Label,
  InputGroup,
  Switch,
  Tabs,
  Tab,
} from '@blueprintjs/core'
import api from '@api'
import { Avatar, Button, Details, Link, Toaster, MultiHRLocationSelect } from '@ui'
import useSettings, { IUseSettings } from './hooks/useSettings'
import { useDropzone } from 'react-dropzone'
import { useUser } from '@hooks'
import { ApolloError } from 'apollo-boost'
import { RouteComponentProps } from '@reach/router'
import queryString from 'query-string'
import usePasswordPolicies, { PasswordPoliciesHook } from './hooks/usePasswordPolicies'
import css from './settings.module.scss'
import PasswordRequirement from './PasswordRequirement'

interface IProps {
  onClose: () => void
}
export default function Settings({ onClose }: IProps) {
  const { user, reload } = useUser()
  const hook = useSettings()
  const policies = usePasswordPolicies()

  useEffect(
    useCallback(() => {
      hook.setData({ user: { ...user } })
    }, [hook, user]),
    [user]
  )

  return (
    <Dialog isOpen onClose={onClose} title="My Settings">
      <div className={Classes.DIALOG_BODY}>
        <Tabs onChange={(tab) => hook.setData({ tab })} selectedTabId={hook.tab}>
          <Tab title="Profile" id="profile" panel={<Profile {...{ hook, reloadUser: reload }} />} />
          <Tab title="Company" id="company" panel={<Company hook={hook} />} />
          <Tab
            title="Security"
            id="security"
            panel={<Security hook={hook} policies={policies} />}
          />
        </Tabs>
      </div>
    </Dialog>
  )
}

interface WithPolicies {
  policies: PasswordPoliciesHook
}

interface IPanelProps {
  hook: IUseSettings
  reloadUser?: () => void
}
function Profile({ hook, reloadUser }: IPanelProps) {
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.*'],
    },
    onDrop: (files: any[], rejected: any[]) => {
      if (!files.length) return
      const reader = new FileReader()
      reader.readAsDataURL(files[0])
      reader.onload = () => {
        if (typeof reader.result !== 'string') return
        const promise = get(hook, 'user.employee.assets.length')
          ? api.employee.asset.update({
              id: get(hook, 'user.employee.assets[0].id', ''),
              content: reader.result,
            })
          : api.employee.asset.create({
              id: get(hook, 'user.employee.id', ''),
              content: reader.result,
            })

        promise
          .then(() => {
            reloadUser && reloadUser()
            Toaster.success({
              message: 'Successfully updated profile picture.',
            })
          })
          .catch((error: ApolloError) => Toaster.fail({ error }))
      }
    },
  })

  const updateUnits = (unit: 'sqft' | 'sqm') => () => {
    const old = get(hook, 'user.context.units')
    hook.setPath('user.context.units', unit)
    api.user
      .updateUnits(get(hook, 'user.id', ''), unit)
      .then((id) => {
        Toaster.success({
          message: `Updated perferred units to ${unit}.`,
        })
      })
      .catch((error: ApolloError) => {
        hook.setPath('user.context.units', old)
        Toaster.fail({ error })
      })
  }

  return (
    <div>
      <h4>Photo</h4>
      <div className="f f-align-center" {...getRootProps()}>
        <Avatar
          changeable
          name={get(hook.user, 'employee.name')}
          assets={get(hook.user, 'employee.assets')}
          size={Avatar.Size.MD}
        />
        <Link className="ml2 bold">Change / Upload</Link>
        <input {...getInputProps()} />
      </div>
      <h4 className="mt5 mb0">Units</h4>
      Choose your unit of measurement
      <ControlGroup className="mt2">
        <Button
          disabled={!hook.user}
          style={{ borderRadius: '4px 0 0 4px' }}
          onClick={updateUnits('sqft')}
          intent={get(hook, 'user.context.units') === 'sqft' ? Intent.PRIMARY : Intent.NONE}
          text="Sqft"
        />
        <Button
          disabled={!hook.user}
          style={{ borderRadius: '0 4px 4px 0' }}
          onClick={updateUnits('sqm')}
          intent={get(hook, 'user.context.units') === 'sqm' ? Intent.PRIMARY : Intent.NONE}
          text={
            <>
              M<sup>2</sup>
            </>
          }
        />
      </ControlGroup>
      <div className="mt5">
        <MultiHRLocationSelect
          values={get(hook, 'user.employee.preferredLocations', [])}
          onChange={(locations) => {
            if (!hook.user) return
            const path = 'user.employee.preferredLocations'
            const old = get(hook, path, [])
            hook.setPath(path, locations)
            api.employee.preferredLocations
              .update(
                get(hook, 'user.employee.id', ''),
                locations.map((l) => l.id)
              )
              .then((employee) => {
                hook.setPath(path, employee.preferredLocations)
                Toaster.success({
                  message: 'HR Locations Saved.',
                })
              })
              .catch((error: ApolloError) => {
                hook.setPath(path, old)
                Toaster.fail({ error })
              })
          }}
        />
      </div>
    </div>
  )
}

function Company({ hook }: IPanelProps) {
  return (
    <div>
      <div className="f">
        <div className="f10">
          <h4 className="mt0 mb1">Enable Notifications</h4>
          Turn on to recieve email notifications
        </div>
        <div>
          <Switch
            checked={get(hook, 'user.company.settings.employeeImportNotificationEnabled', false)}
          />
        </div>
      </div>
      <div className="f mt4">
        <div className="f10">
          <h4 className="mt0 mb1">Enable Hotelling Restriction</h4>
          Turn on to restrict hotel desk booking to allocated department
        </div>
        <div>
          <Switch
            checked={get(hook, 'user.company.settings.hotelingDeskDeptEnforcementEnabled', false)}
            // onChange={(e: any) =>
            //   hook.updateCompanySetting(
            //     'hotelingDeskDeptEnforcementEnabled',
            //     e.target.checked
            //   )
            // }
          />
        </div>
      </div>
      <div className="f mt4">
        <div className="f10">
          <h4 className="mt0 mb1">Enforce 2FA</h4>
          Turn on to require two-factor authentication on all accounts
          <br />
          associated with your company.
        </div>
        <div>
          <Switch
            checked={get(hook, 'user.company.settings.enforceAuthAppEnabled', false)}
            // onChange={(e: any) =>
            //   hook.updateCompanySetting(
            //     'enforceAuthAppEnabled',
            //     e.target.checked
            //   )
            // }
          />
        </div>
      </div>
    </div>
  )
}

function Security({ hook, policies }: IPanelProps & WithPolicies) {
  const [oldPassword, setOldPassword] = useState('')

  const closeAuthApp = () => {
    hook.setData({ otpSecretKey: '', showOtpSecretKey: false })
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()

    if (!policies.isValid) return
    api.employee.password
      .update(oldPassword, policies.password)
      .then((message: any) => {
        Toaster.success({ message })
        hook.setData({ showPasswordForm: false })
      })
      .catch((error: ApolloError) => Toaster.fail({ error }))
  }

  return (
    <div>
      <div className="f f-align-center">
        <h4 className="m0">Change Password</h4>
        <Link
          className="ml2 bold"
          onClick={() => hook.setData({ showPasswordForm: !hook.showPasswordForm })}>
          {hook.showPasswordForm ? '' : 'Change'}
        </Link>
      </div>
      <div>Change your login password</div>
      <Collapse isOpen={hook.showPasswordForm}>
        <EmbedForm onSubmit={submit} className="mt2">
          <Details.Password
            editing
            required
            label="Old Password"
            onChange={(e) => setOldPassword(e.target.value)}
            value={oldPassword}
          />
          New Password Must:
          {policies.fetchingPolicies && <Skeleton />}
          {!policies.fetchingPolicies && (
            <PasswordChecklist>
              {policies.list.map((p, i) => (
                <PasswordRequirement key={i} pass={p.complete}>
                  {p.message}
                </PasswordRequirement>
              ))}
            </PasswordChecklist>
          )}
          <Details.Password
            editing
            required
            label="New Password"
            onChange={(e) => policies.setPassword(e.target.value)}
            value={policies.password}
          />
          <Details.Password
            editing
            required
            label="Confirm Password"
            onChange={(e) => policies.setConfirm(e.target.value)}
            value={policies.confirm}
          />
          <div className="f">
            <Button
              className="mla"
              disabled={!policies.isValid}
              intent={Intent.PRIMARY}
              text="save"
              type="submit"
            />
          </div>
        </EmbedForm>
      </Collapse>
      <div className="f mt4">
        <div className="f10">
          <h4 className="mt0 mb1">Authenticator App</h4>
          Enable Two-Factor Authentication from an authenticator app
        </div>
        <div className="f f-col f-center">
          {!hook.otpSecretKey && (
            <Button
              text={get(hook, 'user.isAuthAppEnabled') ? 'Disable' : 'Enable'}
              onClick={() => {
                if (get(hook, 'user.isAuthAppEnabled')) return
                api.user
                  .updateAuthApp({})
                  .then(({ otpSecretKey }: any) => hook.setData({ otpSecretKey }))
              }}
            />
          )}
        </div>
      </div>
      <Collapse className="mt2" isOpen={Boolean(hook.otpSecretKey)}>
        {get(hook, 'user.isAuthAppEnabled') ? (
          <DisableAuthApp {...{ hook, close: closeAuthApp }} />
        ) : (
          <EnableAuthApp {...{ hook, close: closeAuthApp }} />
        )}
      </Collapse>
    </div>
  )
}

// TODO: Aphrodite login code, safe to remove once Aphrodite is gone.
export function EnableAuthAppAphrodite(props: RouteComponentProps) {
  const search = queryString.parse(get(props, 'location.search', ''))
  const hook = useSettings({
    user: { employee: { email: search.email } },
    otpSecretKey: search.otpSecret,
  })

  return (
    <EnableAuthApp
      {...{
        hook,
        close: () => {
          window.parent.postMessage('2FA_CLOSE', '*')
        },
        onSubmit: () => {
          fetch(`${window.graphqlEndpoint}/set_otp_secret`, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: search.email,
              password: hook.otpPassword,
              otp: hook.otp,
              otpSecretKey: hook.otpSecretKey,
            }),
          })
            .then((r: any) => r.json())
            .then((r: any) => {
              if (r.error) return window.parent.postMessage({ error: r.error }, '*')
              window.parent.postMessage('2FA_DONE', '*')
            })
        },
      }}
    />
  )
}

export function EnableAuthApp({
  hook,
  close,
  onSubmit,
}: IPanelProps & { close: () => void; onSubmit?: () => void }) {
  return (
    <EmbedForm
      onSubmit={(e: any) => {
        e.preventDefault()
        if (onSubmit) return onSubmit()
        api.user
          .updateAuthApp({
            password: get(hook, 'otpPassword'),
            otpSecretKey: get(hook, 'otpSecretKey'),
            otp: get(hook, 'otp'),
          })
          .then(({ status }) => {
            Toaster.success({ message: status })
            close()
          })
          .catch((error: any) => Toaster.fail({ error }))
      }}>
      <h3 className="mv1">Scan this barcode with your app</h3>
      <p>
        Scan the image above with the two-factor authentication app on your phone. If you can’t use
        a barcode,{' '}
        <Link onClick={() => hook.setData({ showOtpSecretKey: true })}>enter this text code</Link>{' '}
        instead.
      </p>
      {hook.showOtpSecretKey && (
        <Label className="mt2">
          Your secret key
          <InputGroup readOnly value={hook.otpSecretKey.toLowerCase()} />
        </Label>
      )}
      <h4 className="mb1">Enter the six-digit code from the application</h4>
      <p>
        After scanning the barcode image, the app will display a six-digit code that you can enter
        below.
      </p>
      <InputGroup
        type="number"
        className="mv2"
        placeholder="Authenticator Code: 123456"
        onChange={(e: any) => {
          hook.setData({ otp: e.target.value })
        }}
      />
      <InputGroup
        type="password"
        className="mv2"
        placeholder="Current Password"
        onChange={(e: any) => hook.setData({ otpPassword: e.target.value })}
      />
      <div className="f">
        <Button className="mla mr2" onClick={close} text="Cancel" />
        <Button type="submit" text="Enable" intent={Intent.PRIMARY} />
      </div>
    </EmbedForm>
  )
}
function DisableAuthApp({ hook, close }: IPanelProps & { close: () => void }) {
  return (
    <EmbedForm
      onSubmit={(e: any) => {
        e.preventDefault()
        api.user
          .updateAuthApp({
            password: get(hook, 'otpPassword'),
            otp: get(hook, 'otp'),
          })
          .then(({ status }) => {
            Toaster.success({ message: status })
            close()
          })
      }}>
      <h3 className="mv1">Remove App Based Two-Factor Authentication</h3>
      <p>
        Enter your current six-digit auth app code, and your current password to remove app based
        authentication from your account.
      </p>
      <InputGroup
        className="mv2"
        placeholder="Authenticator Code: 123456"
        onChange={(e: any) => {
          hook.setData({ otp: e.target.value })
        }}
      />
      <InputGroup
        type="password"
        className="mv2"
        placeholder="Current Password"
        onChange={(e: any) => hook.setData({ otpPassword: e.target.value })}
      />
      <div className="f">
        <Button className="mla mr2" onClick={close} text="Cancel" />
        <Button type="submit" text="Disable" intent={Intent.PRIMARY} />
      </div>
    </EmbedForm>
  )
}

export const EmbedForm: FC<
  React.PropsWithChildren<
    React.DetailedHTMLProps<React.FormHTMLAttributes<HTMLFormElement>, HTMLFormElement>
  >
> = ({ className, ...props }) => {
  return <form className={cx(css.embed_form, className)} {...props} />
}

export const PasswordChecklist: FC<
  React.PropsWithChildren<
    React.DetailedHTMLProps<React.HTMLAttributes<HTMLUListElement>, HTMLUListElement>
  >
> = ({ className, ...props }) => {
  return <ul className={cx(css.password_checklist, className)} {...props} />
}

function Skeleton() {
  return (
    <div className="skeleton">
      <span className={Classes.SKELETON}>Placeholder text</span>
      <span className={Classes.SKELETON}>Placeholder text</span>
      <span className={Classes.SKELETON}>Placeholder text</span>
    </div>
  )
}
